import { JSX, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import {
  EyeOutlined,
  AuditOutlined,
  EditOutlined,
  DeleteOutlined,
  RollbackOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Menu, Button, Tooltip, Dropdown, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { SetURLSearchParams } from "react-router-dom";
import { journalsQueries } from "@/entities/journal";
import { RecallSignIcon, HistoryIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { queryClient } from "@/shared/lib/react-query";
import {
  CommentChip,
  JournalCryptographyWrapper,
  RecordStatus,
  statusIcons,
  UserBubble,
} from "@/shared/ui";
import {
  fallbackString,
  formatDate,
  formatMoscowTime,
  getLatestIsoDate,
} from "@/shared/utils";
import "./get-section-four-columns.scss";
import { type ModalState, SectionFourActionTypes } from "../types";
import { SECTION_FOUR_CONTROL_REPRESENTATIVE_ROLE } from "./format-section-four-signer-label";
import { SectionFourRow } from "./get-section-four-data-source";

type RecordType = SectionFourRow & {
  itemInfo: {
    itemNoVersionId: string;
    sequenceNumber: number;
    createDate?: string;
    updateDate?: string;
    comments?: { comment: string; itemPart?: string }[];
    currentVersionId?: string;
    itemPartOne: {
      id: string;
      status: string;
      updateDate?: string;
      isDefects: boolean;
      defectFixingPlanDate?: string | null;
      defectRecordDate?: string | null;
      controlEventPeriod?: { beginDate?: string; endDate?: string };
      inspectionEmployee?: {
        inspectionEmployeeId: string;
        firstName: string;
        surname: string;
        middleName: string;
        position: string;
        mchdId?: string;
      };
    };
    itemPartTwo?: {
      id: string;
      status: string;
      updateDate?: string;
      defectFixingFactDate?: string;
      defectFixingInspectionEmployee?: {
        defectFixingInspectionEmployeeId: string;
        firstName: string;
        surname: string;
        middleName: string;
        position: string;
        mchdId?: string;
      };
    } | null;
  };
};

type ColumnsContext = {
  setModalState: Dispatch<SetStateAction<ModalState>>;
  navigate: (path: string) => void;
  projectId: string;
  objectId: string;
  mainJournalId: string;
  titleId: string;
  localUser: any;
  setSearchParams: SetURLSearchParams;
  withSignedStringPartOne: (
    params: {
      sectionFourListId: string;
      itemNoVersionId: string;
      signerEmployeeId: string;
      itemPartOneId: string;
      onSuccess?: () => void;
    },
    signedXml: string,
    mchdId?: string,
  ) => Promise<void>;
  withSignedStringPartTwo: (
    params: {
      sectionFourListId: string;
      itemNoVersionId: string;
      signerEmployeeId: string;
      itemPartTwoId: string;
      onSuccess?: () => void;
    },
    signedXml: string,
    mchdId?: string,
  ) => Promise<void>;
  setupSigningStringPartOne: (
    params: {
      sectionFourListId: string;
      itemNoVersionId: string;
      signerEmployeeId: string;
      itemPartOneId: string;
    },
    onOpen: () => void,
  ) => void;
  setupSigningStringPartTwo: (
    params: {
      sectionFourListId: string;
      itemNoVersionId: string;
      signerEmployeeId: string;
      itemPartTwoId: string;
    },
    onOpen: () => void,
  ) => void;
  signingString: string;
  mainJournalData: any;
  isSigningPending: boolean;
  setVersionModalItemId: Dispatch<SetStateAction<string | null>>;
  appRoutes: ReturnType<
    typeof import("@shared/lib/react-router/use-app-routes").useAppRoutes
  >;
  bulkSendMode?: boolean;
};

const COLUMN_TOOLTIPS = {
  controlEventPeriod: "Сроки проведения контрольного мероприятия",
  constructionControlInfo: "Сведения о проведении строительного контроля",
  defects: "Описание выявленных недостатков",
  plannedFixAndSigner:
    "Срок устранения нарушений и уполномоченный представитель по вопросам строительного контроля",
  actualFixAndSigner:
    "Фактическая дата устранения нарушений и уполномоченный представитель по вопросам строительного контроля",
};

const renderColumnTitle = (label: string, tooltip?: string) =>
  tooltip ? (
    <Tooltip title={tooltip}>
      <span className="journals-section-fourth-table__column-title">{label}</span>
    </Tooltip>
  ) : (
    label
  );

const renderDatetimeCell = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  const formattedDate = formatDate(date);
  const formattedTime = formatMoscowTime(date);

  if (!formattedDate && !formattedTime) {
    return "-";
  }

  return (
    <div className="journals-section-fourth-table__datetime">
      {formattedDate ? (
        <span className="journals-section-fourth-table__datetime-date">
          {formattedDate}
        </span>
      ) : null}
      {formattedTime ? (
        <span className="journals-section-fourth-table__datetime-time">
          {formattedTime} МСК
        </span>
      ) : null}
    </div>
  );
};

const renderDateCell = (date?: string | null) => {
  if (!date) {
    return "-";
  }

  const formattedDate = formatDate(date);

  if (!formattedDate) {
    return "-";
  }

  return (
    <div className="journals-section-fourth-table__datetime">
      <span className="journals-section-fourth-table__datetime-date">
        {formattedDate}
      </span>
    </div>
  );
};

const renderControlEventPeriod = (
  period?: { beginDate?: string; endDate?: string } | null,
) => {
  const beginDate = period?.beginDate;
  const endDate = period?.endDate;

  if (!beginDate && !endDate) {
    return "-";
  }

  const beginFormatted = formatDate(beginDate);
  const endFormatted = formatDate(endDate);
  const beginTime = formatMoscowTime(beginDate);
  const endTime = formatMoscowTime(endDate);

  if (!beginFormatted && !endFormatted) {
    return "-";
  }

  return (
    <div className="journals-section-fourth-table__period">
      <span className="journals-section-fourth-table__period-dates">
        {beginFormatted && endFormatted
          ? `${beginFormatted} – ${endFormatted}`
          : fallbackString(beginFormatted || endFormatted)}
      </span>
      {beginTime || endTime ? (
        <span className="journals-section-fourth-table__datetime-time">
          {beginTime && endTime
            ? `${beginTime} – ${endTime} МСК`
            : `${beginTime || endTime} МСК`}
        </span>
      ) : null}
    </div>
  );
};

const renderNumberCell = (record: RecordType) => {
  const updateDate = getLatestIsoDate(
    record.itemInfo?.updateDate,
    record.itemInfo?.itemPartOne?.updateDate,
    record.itemInfo?.itemPartTwo?.updateDate,
  );

  return (
    <div className="journals-section-fourth-table__number">
      <span className="journals-section-fourth-table__number-value">
        {record.sequenceNumber ?? "-"}
      </span>
      {updateDate ? (
        <>
          <span className="journals-section-fourth-table__number-updated">
            изменено {formatDate(updateDate)}
          </span>
          {formatMoscowTime(updateDate) ? (
            <span className="journals-section-fourth-table__datetime-time">
              {formatMoscowTime(updateDate)} МСК
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const getShortFio = (person?: {
  surname?: string;
  firstName?: string;
  middleName?: string;
}) => {
  if (!person) return "—";
  return `${person.surname || ""} ${person.firstName?.[0] || ""}. ${person.middleName?.[0] || ""}.`.trim();
};

const stopRowClick = (event: MouseEvent) => {
  event.stopPropagation();
};

const renderActionsDropdown = (
  editMenu: JSX.Element,
  viewMenu: JSX.Element,
  bulkSendMode?: boolean,
) => {
  if (bulkSendMode) {
    return null;
  }
  return (
  <>
    <Can I="edit" a="section_4">
      <Dropdown overlay={editMenu} trigger={["click"]} placement="bottomRight">
        <div className="journal-table-dropdown-trigger" onClick={stopRowClick}>
          <MoreOutlined />
        </div>
      </Dropdown>
    </Can>
    <Can not I="edit" a="section_4">
      <Dropdown overlay={viewMenu} trigger={["click"]} placement="bottomRight">
        <div className="journal-table-dropdown-trigger" onClick={stopRowClick}>
          <MoreOutlined />
        </div>
      </Dropdown>
    </Can>
  </>
  );
};

const buildMenuItems = (
  items: { key: string; label: string; icon: JSX.Element; action: string }[],
  onClick: (action: string) => void,
) =>
  items.map((item) => ({
    key: item.key,
    label: (
      <div
        className="journal-table-menu-item"
        onClick={() => onClick(item.action)}
      >
        {item.icon}
        <span className="journal-table-menu-label">{item.label}</span>
      </div>
    ),
  }));

const getPartOneStatusItems = (status: RecordStatus) => {
  const items: {
    key: string;
    label: string;
    icon: JSX.Element;
    action: string;
  }[] = [];

  if (status === "NEW") {
    items.push(
      {
        key: "send",
        label: "Отправить на подписание",
        icon: <AuditOutlined />,
        action: "SEND",
      },
      {
        key: "edit",
        label: "Редактировать",
        icon: <EditOutlined />,
        action: "EDIT",
      },
      {
        key: "delete",
        label: "Удалить",
        icon: <DeleteOutlined />,
        action: "DELETE",
      },
    );
  }

  if (status === "AWAITING_SIGNATURE") {
    items.push({
      key: "recall",
      label: "Отозвать с подписания",
      icon: <RecallSignIcon />,
      action: "RECALL",
    });
  }

  if (status === "SIGNED") {
    items.push({
      key: "edit",
      label: "Редактировать",
      icon: <EditOutlined />,
      action: "EDIT",
    });
  }

  if (status === "REJECTED") {
    items.push({
      key: "return",
      label: "Вернуть в работу",
      icon: <RollbackOutlined />,
      action: "RETURN",
    });
  }

  return items;
};

const getPartTwoStatusItems = (status: RecordStatus) => {
  if (status === "NEW") {
    return [
      {
        key: "send",
        label: "Отправить на подписание",
        icon: <AuditOutlined />,
        action: "SEND",
      },
      {
        key: "edit",
        label: "Редактировать",
        icon: <EditOutlined />,
        action: "EDIT",
      },
    ];
  }

  if (status === "AWAITING_SIGNATURE") {
    return [
      {
        key: "recall",
        label: "Отозвать с подписания",
        icon: <RecallSignIcon />,
        action: "RECALL",
      },
    ];
  }

  if (status === "SIGNED") {
    return [
      {
        key: "edit",
        label: "Редактировать",
        icon: <EditOutlined />,
        action: "EDIT",
      },
    ];
  }

  if (status === "REJECTED") {
    return [
      {
        key: "return",
        label: "Вернуть в работу",
        icon: <RollbackOutlined />,
        action: "RETURN",
      },
    ];
  }

  return [];
};

const renderPartOneCombinedCell = (
  record: RecordType,
  ctx: ColumnsContext,
) => {
  const { itemPartOne } = record.itemInfo;
  const signer = itemPartOne.inspectionEmployee;
  const allComments = record.itemInfo.comments || [];
  const comments = allComments.filter(
    (comment: { itemPart?: string }) => comment?.itemPart === itemPartOne.id,
  );
  const planDate =
    itemPartOne.defectFixingPlanDate || itemPartOne.defectRecordDate;

  const isMyEntry =
    signer?.inspectionEmployeeId === ctx.localUser?.user_id &&
    itemPartOne.status === "AWAITING_SIGNATURE";

  const handleMenuClick = (action: string) => {
    switch (action) {
      case "SEND":
        ctx.setModalState({
          visible: true,
          title: "Отправка записи на подписание",
          info: "Вы действительно хотите отправить запись на подписание?",
          confirmText: "Отправить",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: false,
          action: "SEND",
        });
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
          itemPartOne: itemPartOne.id,
        });
        break;
      case "REJECT":
        ctx.setModalState({
          visible: true,
          title: "Отклонить запись",
          info: "Вы действительно хотите отклонить запись?",
          confirmText: "Отклонить",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: true,
          action: "REJECT",
          signerEmployeeId: signer?.inspectionEmployeeId,
        });
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
          itemPartOne: itemPartOne.id,
        });
        break;
      case "DELETE":
        ctx.setModalState({
          visible: true,
          title: "Удаление записи",
          info: "Вы уверены, что хотите удалить эту запись?",
          confirmText: "Удалить",
          cancelText: "Отмена",
          signerEmployeeId: signer?.inspectionEmployeeId,
          action: "DELETE",
        });
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
        });
        break;
      case "RETURN":
        ctx.setModalState({
          visible: true,
          title: "Вернуть запись в работу",
          info: "Вы действительно хотите вернуть запись в статус Новый?",
          confirmText: "Вернуть",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: false,
          action: "RETURN",
          signerEmployeeId: signer?.inspectionEmployeeId,
        });
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
          itemPartOne: itemPartOne.id,
        });
        break;
      case "EDIT":
        ctx.navigate(
          ctx.appRoutes.journals.section4Form(
            ctx.projectId,
            ctx.objectId,
            ctx.mainJournalId,
            ctx.titleId,
            record.itemInfo.itemNoVersionId,
          ),
        );
        break;
      case "RECALL":
        ctx.setModalState({
          visible: true,
          title: "Отозвать с подписания",
          info: "Вы действительно хотите отменить подписание?",
          confirmText: "Отозвать",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: false,
          action: "RECALL",
          signerEmployeeId: signer?.inspectionEmployeeId,
        });
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
          itemPartOne: itemPartOne.id,
        });
        break;
      case "VERSIONS":
        ctx.setSearchParams({
          itemNoVersionId: record.itemInfo.itemNoVersionId,
          action: "VERSION",
        });
        ctx.setVersionModalItemId(record.itemInfo.itemNoVersionId);
        break;
      case "VIEW":
        ctx.navigate(
          ctx.appRoutes.journals.section4View(
            ctx.projectId,
            ctx.objectId,
            ctx.mainJournalId,
            ctx.titleId,
            record.itemInfo.itemNoVersionId,
          ),
        );
        break;
      default:
        break;
    }
  };

  const baseItems = [
    {
      key: "view",
      label: "Открыть запись",
      icon: <EyeOutlined />,
      action: "VIEW",
    },
    ...(record.itemInfo.currentVersionId
      ? [
          {
            key: "versions",
            label: "Версии",
            icon: <HistoryIcon />,
            action: "VERSIONS",
          },
        ]
      : []),
  ];

  const editItems = [
    ...baseItems,
    ...getPartOneStatusItems(itemPartOne.status as RecordStatus),
  ];

  return (
    <div className="journal-table-datetime-actions-cell">
      <div className="journals-section-fourth-table__combined-cell">
        {renderDateCell(planDate)}
        <div className="journal-table-signer-column journals-section-fourth-table__signer-block">
          <div className="journal-table-signer-row">
            <UserBubble userId={signer?.inspectionEmployeeId}>
              <Button type="link" style={{ padding: 0, marginRight: 8 }}>
                {getShortFio(signer)}
              </Button>
            </UserBubble>
            {statusIcons[itemPartOne.status as keyof typeof statusIcons] ? (
              <div className="journal-table-status-icon-wrap">
                {statusIcons[itemPartOne.status as keyof typeof statusIcons]}
              </div>
            ) : null}
            {comments.length > 0 ? (
              <div className="journal-table-comment-wrap">
                <Tooltip
                  title={comments
                    .map((c: { comment: string }) => c.comment)
                    .join("\n")}
                >
                  <span className="journal-table-comment-trigger">
                    <CommentChip className="comment-chip--table" />
                  </span>
                </Tooltip>
              </div>
            ) : null}
          </div>
          <Typography.Text
            type="secondary"
            className="journals-section-fourth-table__signer-role"
          >
            {SECTION_FOUR_CONTROL_REPRESENTATIVE_ROLE}
          </Typography.Text>
          {itemPartOne.status === "SIGNED" && signer?.mchdId ? (
            <Typography.Text type="secondary">
              Доверенность № {signer.mchdId}
            </Typography.Text>
          ) : null}
          {isMyEntry && !ctx.bulkSendMode ? (
            <div className="journal-table-sign-actions">
              <JournalCryptographyWrapper
                currentUser={ctx.localUser}
                signContent={ctx.signingString}
                onSuccess={(xml, _proxyId, mchdId) =>
                  ctx.withSignedStringPartOne(
                    {
                      sectionFourListId:
                        ctx.mainJournalData?.mainJournal?.section4
                          ?.sectionListId || "",
                      itemNoVersionId: record.itemInfo.itemNoVersionId,
                      signerEmployeeId: ctx.localUser?.user_id || "",
                      onSuccess: () =>
                        queryClient.invalidateQueries({
                          queryKey: journalsQueries.getSection4({
                            section4ListId:
                              ctx.mainJournalData?.mainJournal?.section4
                                ?.sectionListId || "",
                          }).queryKey,
                        }),
                      itemPartOneId: itemPartOne.id,
                    },
                    xml,
                    mchdId,
                  )
                }
              >
                {({ onOpen }) => (
                  <Button
                    type="primary"
                    loading={ctx.isSigningPending}
                    style={{ marginRight: 10 }}
                    onClick={() =>
                      ctx.setupSigningStringPartOne(
                        {
                          sectionFourListId:
                            ctx.mainJournalData?.mainJournal?.section4
                              ?.sectionListId || "",
                          itemNoVersionId: record.itemInfo.itemNoVersionId,
                          signerEmployeeId: ctx.localUser?.user_id || "",
                          itemPartOneId: itemPartOne.id,
                        },
                        onOpen,
                      )
                    }
                  >
                    Подписать
                  </Button>
                )}
              </JournalCryptographyWrapper>
              <Button
                type="dashed"
                onClick={() => {
                  ctx.setModalState({
                    visible: true,
                    title: "Отклонить с подписания",
                    info: "Вы действительно хотите отклонить подписание?",
                    confirmText: "Отклонить",
                    cancelText: "Отмена",
                    inputPlaceholder: "Комментарий",
                    inputValue: "",
                    isInputModal: true,
                    action: "REJECT",
                    signerEmployeeId: signer?.inspectionEmployeeId,
                  });
                  ctx.setSearchParams({
                    itemNoVersionId: record.itemInfo.itemNoVersionId,
                    itemPartOne: itemPartOne.id,
                  });
                }}
              >
                Отклонить
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {renderActionsDropdown(
        <Menu items={buildMenuItems(editItems, handleMenuClick)} />,
        <Menu items={buildMenuItems(baseItems, handleMenuClick)} />,
        ctx.bulkSendMode,
      )}
    </div>
  );
};

const renderPartTwoCombinedCell = (
  record: RecordType,
  ctx: ColumnsContext,
) => {
  const { itemPartOne, itemPartTwo, itemNoVersionId } = record.itemInfo;

  const handleMenuClick = (action: string) => {
    const signer = itemPartTwo?.defectFixingInspectionEmployee;

    switch (action) {
      case "SEND":
        ctx.setModalState({
          visible: true,
          title: "Отправка записи на подписание",
          info: "Вы действительно хотите отправить запись на подписание?",
          confirmText: "Отправить",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: false,
          action: "SEND",
          sectionTwoAction: SectionFourActionTypes.STATUS,
        });
        ctx.setSearchParams({
          itemNoVersionId,
          itemPartTwo: itemPartTwo?.id || "",
        });
        break;
      case "REJECT":
        ctx.setModalState({
          visible: true,
          title: "Отклонить запись",
          info: "Вы действительно хотите отклонить запись?",
          confirmText: "Отклонить",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: true,
          action: "REJECT",
          signerEmployeeId: signer?.defectFixingInspectionEmployeeId,
          sectionTwoAction: SectionFourActionTypes.STATUS,
        });
        ctx.setSearchParams({
          itemNoVersionId,
          itemPartTwo: itemPartTwo?.id || "",
        });
        break;
      case "RETURN":
        ctx.setModalState({
          visible: true,
          title: "Вернуть запись в работу",
          info: "Вы действительно хотите вернуть запись в статус Новый?",
          confirmText: "Вернуть",
          cancelText: "Отмена",
          inputPlaceholder: "Комментарий",
          inputValue: "",
          isInputModal: false,
          action: "RETURN",
          signerEmployeeId: signer?.defectFixingInspectionEmployeeId,
          sectionTwoAction: SectionFourActionTypes.EDIT,
        });
        ctx.setSearchParams({
          itemNoVersionId,
          itemPartTwo: itemPartTwo?.id || "",
        });
        break;
      case "VERSIONS":
        ctx.setVersionModalItemId(itemNoVersionId);
        break;
      case "EDIT": {
        const basePath = ctx.appRoutes.journals.section4View(
          ctx.projectId,
          ctx.objectId,
          ctx.mainJournalId,
          ctx.titleId,
          itemNoVersionId,
        );
        const params = new URLSearchParams();
        params.set("sectionTwoAction", SectionFourActionTypes.EDIT);
        params.set("itemPartTwo", itemPartTwo?.id || "");
        ctx.navigate(`${basePath}?${params.toString()}`);
        break;
      }
      case "VIEW":
        ctx.navigate(
          ctx.appRoutes.journals.section4View(
            ctx.projectId,
            ctx.objectId,
            ctx.mainJournalId,
            ctx.titleId,
            itemNoVersionId,
          ),
        );
        break;
      default:
        break;
    }
  };

  const baseItems = [
    {
      key: "view",
      label: "Открыть запись",
      icon: <EyeOutlined />,
      action: "VIEW",
    },
    ...(record.itemInfo.currentVersionId
      ? [
          {
            key: "versions",
            label: "Версии",
            icon: <HistoryIcon />,
            action: "VERSIONS",
          },
        ]
      : []),
  ];

  const editItems = itemPartTwo
    ? [
        ...baseItems,
        ...getPartTwoStatusItems(itemPartTwo.status as RecordStatus),
      ]
    : baseItems;

  if (!itemPartOne.isDefects) {
    return (
      <div className="journal-table-datetime-actions-cell">
        <span className="journals-section-fourth-table__no-defects">
          Замечания не выявлены
        </span>
        {renderActionsDropdown(
          <Menu items={buildMenuItems(editItems, handleMenuClick)} />,
          <Menu items={buildMenuItems(baseItems, handleMenuClick)} />,
          ctx.bulkSendMode,
        )}
      </div>
    );
  }

  if (
    !itemPartTwo &&
    itemPartOne.isDefects &&
    itemPartOne.status === "SIGNED"
  ) {
    return (
      <div className="journal-table-datetime-actions-cell">
        {!ctx.bulkSendMode ? (
        <Can I="edit" a="section_4">
          <Button
            type="primary"
            onClick={() => {
              const basePath = ctx.appRoutes.journals.section4View(
                ctx.projectId,
                ctx.objectId,
                ctx.mainJournalId,
                ctx.titleId,
                itemNoVersionId,
              );
              const params = new URLSearchParams();
              params.set("sectionTwoAction", SectionFourActionTypes.CREATE);
              params.set("itemPartOne", itemPartOne.id);
              ctx.navigate(`${basePath}?${params.toString()}`);
            }}
          >
            Отметить устранение недостатков
          </Button>
        </Can>
        ) : null}
        {renderActionsDropdown(
          <Menu items={buildMenuItems(editItems, handleMenuClick)} />,
          <Menu items={buildMenuItems(baseItems, handleMenuClick)} />,
          ctx.bulkSendMode,
        )}
      </div>
    );
  }

  if (!itemPartTwo) {
    return (
      <div className="journal-table-datetime-actions-cell">
        <span>-</span>
        {renderActionsDropdown(
          <Menu items={buildMenuItems(editItems, handleMenuClick)} />,
          <Menu items={buildMenuItems(baseItems, handleMenuClick)} />,
          ctx.bulkSendMode,
        )}
      </div>
    );
  }

  const signer = itemPartTwo.defectFixingInspectionEmployee;
  const allComments = record.itemInfo.comments || [];
  const comments = allComments.filter(
    (comment: { itemPart?: string }) => comment?.itemPart === itemPartTwo.id,
  );
  const isMyEntry =
    signer?.defectFixingInspectionEmployeeId === ctx.localUser?.user_id &&
    itemPartTwo.status === "AWAITING_SIGNATURE";

  return (
    <div className="journal-table-datetime-actions-cell">
      <div className="journals-section-fourth-table__combined-cell">
        {renderDatetimeCell(itemPartTwo.defectFixingFactDate)}
        <div className="journal-table-signer-column journals-section-fourth-table__signer-block">
          <div className="journal-table-signer-row">
            <UserBubble userId={signer?.defectFixingInspectionEmployeeId}>
              <Button type="link" style={{ padding: 0, marginRight: 8 }}>
                {getShortFio(signer)}
              </Button>
            </UserBubble>
            {statusIcons[itemPartTwo.status as keyof typeof statusIcons] ? (
              <div className="journal-table-status-icon-wrap">
                {statusIcons[itemPartTwo.status as keyof typeof statusIcons]}
              </div>
            ) : null}
            {comments.length > 0 ? (
              <div className="journal-table-comment-wrap">
                <Tooltip
                  title={comments
                    .map((c: { comment: string }) => c.comment)
                    .join("\n")}
                >
                  <span className="journal-table-comment-trigger">
                    <CommentChip className="comment-chip--table" />
                  </span>
                </Tooltip>
              </div>
            ) : null}
          </div>
          <Typography.Text
            type="secondary"
            className="journals-section-fourth-table__signer-role"
          >
            {SECTION_FOUR_CONTROL_REPRESENTATIVE_ROLE}
          </Typography.Text>
          {itemPartTwo.status === "SIGNED" && signer?.mchdId ? (
            <Typography.Text type="secondary">
              Доверенность № {signer.mchdId}
            </Typography.Text>
          ) : null}
          {isMyEntry && !ctx.bulkSendMode ? (
            <div className="journal-table-sign-actions">
              <JournalCryptographyWrapper
                currentUser={ctx.localUser}
                signContent={ctx.signingString}
                onSuccess={(xml, _proxyId, mchdId) =>
                  ctx.withSignedStringPartTwo(
                    {
                      sectionFourListId:
                        ctx.mainJournalData?.mainJournal?.section4
                          ?.sectionListId || "",
                      itemNoVersionId,
                      signerEmployeeId: ctx.localUser?.user_id || "",
                      itemPartTwoId: itemPartTwo.id,
                      onSuccess: () =>
                        queryClient.invalidateQueries({
                          queryKey: journalsQueries.getSection4({
                            section4ListId:
                              ctx.mainJournalData?.mainJournal?.section4
                                ?.sectionListId || "",
                          }).queryKey,
                        }),
                    },
                    xml,
                    mchdId,
                  )
                }
              >
                {({ onOpen }) => (
                  <Button
                    type="primary"
                    loading={ctx.isSigningPending}
                    style={{ marginRight: 10 }}
                    onClick={() =>
                      ctx.setupSigningStringPartTwo(
                        {
                          sectionFourListId:
                            ctx.mainJournalData?.mainJournal?.section4
                              ?.sectionListId || "",
                          itemNoVersionId,
                          signerEmployeeId: ctx.localUser?.user_id || "",
                          itemPartTwoId: itemPartTwo.id,
                        },
                        onOpen,
                      )
                    }
                  >
                    Подписать
                  </Button>
                )}
              </JournalCryptographyWrapper>
              <Button
                type="dashed"
                onClick={() => {
                  ctx.setModalState({
                    visible: true,
                    title: "Отклонить с подписания",
                    info: "Вы действительно хотите отклонить подписание?",
                    confirmText: "Отклонить",
                    cancelText: "Отмена",
                    inputPlaceholder: "Комментарий",
                    inputValue: "",
                    isInputModal: true,
                    action: "REJECT",
                    signerEmployeeId: signer?.defectFixingInspectionEmployeeId,
                    sectionTwoAction: SectionFourActionTypes.STATUS,
                  });
                  ctx.setSearchParams({
                    itemNoVersionId,
                    itemPartTwo: itemPartTwo.id,
                  });
                }}
              >
                Отклонить
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {renderActionsDropdown(
        <Menu items={buildMenuItems(editItems, handleMenuClick)} />,
        <Menu items={buildMenuItems(baseItems, handleMenuClick)} />,
        ctx.bulkSendMode,
      )}
    </div>
  );
};

export const getSectionFourColumns = (
  ctx: ColumnsContext & {
    handleOpenForm: ({
      formType,
      itemNoVersionId,
    }: {
      formType: string | null;
      itemNoVersionId?: string;
    }) => void;
  },
): ColumnsType<SectionFourRow> => [
  {
    title: "№",
    dataIndex: "sequenceNumber",
    key: "sequenceNumber",
    width: 88,
    render: (_: unknown, record: RecordType) => renderNumberCell(record),
  },
  {
    title: renderColumnTitle(
      "Сроки проведения контрольного мероприятия",
      COLUMN_TOOLTIPS.controlEventPeriod,
    ),
    key: "controlEventPeriod",
    width: 160,
    render: (_: unknown, record: RecordType) =>
      renderControlEventPeriod(record.itemInfo.itemPartOne.controlEventPeriod),
  },
  {
    title: renderColumnTitle(
      "Сведения о проведении строительного контроля",
      COLUMN_TOOLTIPS.constructionControlInfo,
    ),
    dataIndex: "info",
    key: "info",
    width: 200,
    className: "journals-section-fourth-table__text-cell",
    render: (text: string) => (
      <span className="journals-section-fourth-table__cell-text">
        {fallbackString(text)}
      </span>
    ),
  },
  {
    title: renderColumnTitle("Выявленные недостатки", COLUMN_TOOLTIPS.defects),
    dataIndex: "defect",
    key: "defect",
    width: 200,
    className: "journals-section-fourth-table__text-cell",
    render: (text: string) => (
      <span className="journals-section-fourth-table__cell-text">
        {fallbackString(text)}
      </span>
    ),
  },
  {
    title: renderColumnTitle(
      "Срок устранения нарушений и ответственный",
      COLUMN_TOOLTIPS.plannedFixAndSigner,
    ),
    key: "plannedFixAndSigner",
    width: 280,
    render: (_: unknown, record: RecordType) =>
      renderPartOneCombinedCell(record, ctx),
    onCell: () => ({
      style: {
        borderRight: "1px solid rgba(235, 235, 235, 1)",
      },
    }),
  },
  {
    title: renderColumnTitle(
      "Фактическая дата устранения нарушений и ответственный",
      COLUMN_TOOLTIPS.actualFixAndSigner,
    ),
    key: "actualFixAndSigner",
    width: 280,
    render: (_: unknown, record: RecordType) =>
      renderPartTwoCombinedCell(record, ctx),
  },
  {
    title: "Дата создания",
    dataIndex: "createDate",
    key: "createDate",
    width: 120,
    render: (_: unknown, record: RecordType) =>
      renderDatetimeCell(record.itemInfo.createDate),
  },
];
